import { useState } from "react";

import { format, parseISO } from "date-fns";
import { sticky } from "tippy.js";

import { postTrackingData } from "@/api/endpoints/track";
import CalendarDaysIcon from "@/assets/icons/calendar-days.svg";
import { FILTER } from "@/constants";
import { useI18n } from "@/contexts/i18nContext";
import useFilterParams from "@/hooks/useFilterParams";
import { EVENT } from "@/tracking_events";
import Button from "@components/Button";
import { CalendarSettings } from "@components/DatePicker/Calendar/types";
import Range, { DateRangePreset } from "@components/DatePicker/Range";
import Tippy from "@tippyjs/react/headless";

import { dateMatchesRange, reverseDateRange } from "./ranges";

import styles from "./DateRangeButton.module.css";

const DateRangeButton: React.FunctionComponent<{
  className?: string;
  ranges: DateRangePreset[];
  settings?: Partial<CalendarSettings>;
}> = ({ className, settings, ranges }) => {
  const [{ start_date, end_date }, setFilters] = useFilterParams();
  const [visible, setVisible] = useState(false);
  const { locale } = useI18n();

  const rangeLabel =
    reverseDateRange(
      start_date ? parseISO(start_date) : undefined,
      end_date ? parseISO(end_date) : undefined,
      locale,
      ranges
    )?.label || "";

  const defaultRange = ranges.find((r) => r.default);

  return (
    <div className={className} data-testid="datepicker-button">
      <Tippy
        visible={visible}
        maxWidth="none"
        onClickOutside={() => setVisible(false)}
        interactive
        plugins={[sticky]}
        sticky
        appendTo={() => document.body}
        render={(attrs) => {
          return (
            <div>
              <div className={styles.container} tabIndex={-1} {...attrs}>
                <Range
                  startDate={
                    start_date ? parseISO(start_date) : defaultRange?.startDate
                  }
                  endDate={
                    end_date ? parseISO(end_date) : defaultRange?.endDate
                  }
                  settings={{
                    locale,
                    ...settings,
                  }}
                  presets={ranges}
                  onChange={({ startDate, endDate }, fromPreset) => {
                    const dates = {
                      start_date: startDate
                        ? format(startDate, "yyyy-MM-dd")
                        : undefined,
                      end_date: endDate
                        ? format(endDate, "yyyy-MM-dd")
                        : undefined,
                    };

                    setFilters({ ...dates, [FILTER.PAGE]: undefined });
                    if (fromPreset) {
                      setVisible(false);

                      const picked = ranges.find((preset) =>
                        dateMatchesRange(startDate, endDate, preset)
                      );
                      postTrackingData(EVENT.DATE_PRESET_CHANGE, {
                        preset: picked?.identifier,
                        ...dates,
                      });
                    } else {
                      postTrackingData(EVENT.DATE_PRESET_CHANGE, dates);
                    }
                  }}
                />
              </div>
            </div>
          );
        }}
        placement="bottom"
      >
        <Button
          type="button"
          variant="secondary"
          icon={CalendarDaysIcon}
          iconPlacement="after"
          onClick={() => setVisible((v) => !v)}
        >
          {rangeLabel}
        </Button>
      </Tippy>
    </div>
  );
};

export default DateRangeButton;
